interface DayStat {
  date: string;
  wordCount: number;
}

interface WordCountChartProps {
  data: DayStat[];
}

export function WordCountChart({ data }: WordCountChartProps) {
  const rawMax = Math.max(...data.map((d) => d.wordCount), 1);
  const today = new Date().toISOString().split('T')[0];

  const width = 600;
  const height = 140;
  const padLeft = 34;
  const padRight = 8;
  const padTop = 8;
  const padBottom = 20;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const n = data.length;

  const yMax = rawMax;
  const yTicks = [0, 1, 2, 3, 4].map((i) => Math.round((yMax / 4) * i));

  function px(i: number) {
    return padLeft + (i / (n - 1)) * chartW;
  }
  function py(wordCount: number) {
    return padTop + chartH - (wordCount / yMax) * chartH;
  }

  const baselineY = padTop + chartH;

  const areaPath = [
    `M ${px(0)} ${baselineY}`,
    ...data.map((d, i) => `L ${px(i)} ${py(d.wordCount)}`),
    `L ${px(n - 1)} ${baselineY}`,
    'Z',
  ].join(' ');

  const linePath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${px(i)} ${py(d.wordCount)}`)
    .join(' ');

  // ~5 evenly spaced x-axis label indices, always include first and last
  const labelIndices = new Set<number>([0, n - 1]);
  const xStep = Math.floor((n - 1) / 4);
  for (let i = xStep; i < n - 1; i += xStep) labelIndices.add(i);

  function formatDateLabel(dateStr: string) {
    const [, m, d] = dateStr.split('-');
    return `${parseInt(m)}/${parseInt(d)}`;
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full select-none">
      {/* Y-axis gridlines + labels */}
      {yTicks.map((tick) => {
        const y = py(tick);
        return (
          <g key={tick}>
            <line
              x1={padLeft} y1={y}
              x2={padLeft + chartW} y2={y}
              stroke="#eaecf0" strokeWidth="1"
            />
            <text
              x={padLeft - 4}
              y={y + 3}
              textAnchor="end"
              fontSize="9"
              fill="#72777d"
            >
              {tick}
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill="#3a68c9" fillOpacity="0.08" />

      {/* Line */}
      <path
        d={linePath}
        fill="none"
        stroke="#3a68c9"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Baseline */}
      <line
        x1={padLeft} y1={baselineY}
        x2={padLeft + chartW} y2={baselineY}
        stroke="#c8ccd1" strokeWidth="1"
      />

      {/* Dots */}
      {data.map((day, i) => (
        <circle
          key={day.date}
          cx={px(i)}
          cy={py(day.wordCount)}
          r={day.date === today ? 4 : 2}
          fill={day.date === today ? '#2a55a5' : '#3a68c9'}
          fillOpacity={day.date === today ? 1 : 0.5}
        />
      ))}

      {/* X-axis labels */}
      {data.map((day, i) =>
        labelIndices.has(i) ? (
          <text
            key={day.date}
            x={px(i)}
            y={height - 4}
            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
            fontSize="9"
            fill="#54595d"
          >
            {formatDateLabel(day.date)}
          </text>
        ) : null
      )}
    </svg>
  );
}
